/**
 * Which charge rate a site is billed at on a date.
 *
 * A rate set on the site wins; otherwise the contract's own rate applies. Rate
 * windows for one site (or one contract) never overlap — the database refuses
 * it — so at most one of each can cover a date.
 */

import type { ChargeRate, ProfitabilitySite } from './types';

export type ChargeRateLookup = (site: ProfitabilitySite, dateKey: string) => ChargeRate | null;

const covers = (rate: ChargeRate, dateKey: string): boolean => (
  rate.effectiveFrom <= dateKey && (rate.effectiveTo === null || dateKey <= rate.effectiveTo)
);

export function buildChargeRateLookup(chargeRates: ChargeRate[]): ChargeRateLookup {
  const bySite = new Map<string, ChargeRate[]>();
  const byContract = new Map<string, ChargeRate[]>();

  chargeRates.forEach((rate) => {
    const index = rate.siteId ? bySite : byContract;
    const key = rate.siteId ?? rate.clientContractId;
    if (!index.has(key)) index.set(key, []);
    index.get(key)!.push(rate);
  });

  return (site, dateKey) => {
    if (!site.clientContractId) return null;

    const siteRate = (bySite.get(site.id) ?? []).find((rate) => (
      rate.clientContractId === site.clientContractId && covers(rate, dateKey)
    ));
    if (siteRate) return siteRate;

    return (byContract.get(site.clientContractId) ?? []).find((rate) => covers(rate, dateKey)) ?? null;
  };
}

/** The rate in force on a date for a contract as a whole, ignoring site overrides. */
export function contractWideRate(
  chargeRates: ChargeRate[],
  clientContractId: string,
  dateKey: string,
): ChargeRate | null {
  return chargeRates.find((rate) => (
    rate.clientContractId === clientContractId && rate.siteId === null && covers(rate, dateKey)
  )) ?? null;
}
